<template>
  <div
    class="field-group-content"
    :class="customClass"
  >
    <div
      v-if="fieldGroup.canSave && fieldGroup.elementPartType"
      class="flex justify-content-end gap-2 mt-2"
    >
      <SpmButton
        v-if="!selectedSyncElement && componentInSyncPart"
        v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.edit`)"
        class="p-button p-button-secondary"
        icon="far fa-pen-to-square"
        @click="enterEditSyncElement"
      />
      <SpmButton
        v-if="!hideLoadElementBtn"
        v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.load`)"
        class="p-button p-button-secondary"
        icon="far fa-upload"
        @click="handleShowElementModal"
      />
      <SpmButton
        v-if="!componentInSyncPart && canSaveStyle"
        v-tooltip="t(`templateBuilder.structureActions.${fieldGroup.elementPartType}.save`)"
        class="p-button p-button-secondary"
        icon="far fa-star"
        @click="handleShowSaveElementModal"
      />
      <HelpMessage
        v-if="!hideLoadElementBtn"
        v-tooltip="t('showHelpArticle')"
        class="flex align-items-center"
        :link="{
          type: 'gleap',
          articleId: '166',
          label: ''
        }"
      />
    </div>
    <div
      v-for="field of fieldGroupItems"
      :key="field.label"
      class="field-wrapper"
    >
      <component
        :is="field.componentName"
        v-if="!subGroupLabel && getFieldParserValues(field.label)"
        :configs="field"
        :parser-values="getFieldParserValues(field.label)"
        :tab-item-label="tabItem.label"
        :field-group-label="fieldGroup.label"
        :id-campaign="idCampaign"
        :origin-call="originCall"
        :disable-handler="field.disableHandler ?
          () => field.disableHandler && field.disableHandler(getFieldParserValues(field.label)?.selector)
          : null"
        :disable-message="field.disableMessage ? field.disableMessage : ''"
        :element-part-type="fieldGroup.elementPartType || ''"
        @on-change-properties="(data) => onFieldChange(data, field.label)"
      />
      <component
        :is="field.componentName"
        v-else-if="subGroupLabel && getFieldParserValues(field.label, true)"
        :configs="field"
        :parser-values="getFieldParserValues(field.label, true)"
        :tab-item-label="tabItem.label"
        :field-group-label="fieldGroup.label"
        :id-campaign="idCampaign"
        :origin-call="originCall"
        :disable-handler="field.disableHandler ?
          () => field.disableHandler && field.disableHandler(getFieldParserValues(field.label, true)?.selector)
          : null"
        :disable-message="field.disableMessage ? field.disableMessage : ''"
        :element-part-type="fieldGroup.elementPartType || ''"
        @on-change-properties="(data) => onNestedFieldChange(data, field.label)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  ref,
  nextTick,
  watch,
} from 'vue';

import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';

import { useStore } from '@/store';

import {
  TabItem,
  TabSubitem,
  ParserObject,
  FieldsGroup,
  TemplateStructureEnum,
  SelectedStructureSyncPart,
} from '@/types';

import Tooltip from 'primevue/tooltip';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';

import StructureEdit from '@/components/template-builder/fields/StructureEdit.vue';
import ButtonGroup from '@/components/template-builder/fields/ButtonGroup.vue';
import DraggableButtonGroup from '@/components/template-builder/fields/DraggableButtonGroup.vue';
import Stepper from '@/components/template-builder/fields/Stepper.vue';
import StepperPxPercentSlider from '@/components/template-builder/fields/StepperPxPercentSlider.vue';
import Color from '@/components/template-builder/fields/Color.vue';
import Spaces from '@/components/template-builder/fields/Spaces.vue';
import FontFamily from '@/components/template-builder/fields/FontFamily.vue';
import Borders from '@/components/template-builder/fields/Borders.vue';
import InputVariables from '@/components/template-builder/fields/InputVariables.vue';
import Code from '@/components/template-builder/fields/Code.vue';
import WysiwygEditor from '@/components/template-builder/fields/WysiwygEditor.vue';
import Link from '@/components/template-builder/fields/Link.vue';
import Dropdown from '@/components/template-builder/fields/Dropdown.vue';
import BoxShadow from '@/components/template-builder/fields/BoxShadow.vue';
import Information from '@/components/template-builder/fields/Information.vue';
import InputGroup from '@/components/template-builder/fields/InputGroup.vue';
import SharingType from '@/components/template-builder/fields/SharingType.vue';
import TextareaVariables from '@/components/template-builder/fields/TextareaVariables.vue';
import ItemList from '@/components/template-builder/fields/ItemList.vue';
import TriggerButton from '@/components/template-builder/fields/TriggerButton.vue';
import MultiDropdown from '@/components/template-builder/fields/MultiDropdown.vue';
import SelectionType from '@/components/template-builder/fields/SelectionType.vue';
import ActionType from '@/components/template-builder/fields/ActionType.vue';
import Image from '@/components/template-builder/fields/Image.vue';
import FacebookButtons from '@/components/template-builder/fields/FacebookButtons.vue';
import LineHeight from '@/components/template-builder/fields/LineHeight.vue';
import Font from '@/components/template-builder/fields/Font.vue';
import SocialIconsStyle from '@/components/template-builder/fields/SocialIconsStyle.vue';
import BackgroundImage from '@/components/template-builder/fields/BackgroundImage.vue';
import CustomHtml from '@/components/template-builder/fields/CustomHtml.vue';
import { TemplateEditorState, handlePropertyValuesChange, showElementsModal } from '@/composables/template-editor/TemplateEditor';
import { getTemplateIframeDocument } from '../utils/helpers';

export default defineComponent({
  name: 'ConfigurationPanelTabContentFieldGroup',

  components: {
    StructureEdit,
    SpmButton,
    ButtonGroup,
    DraggableButtonGroup,
    Stepper,
    StepperPxPercentSlider,
    Color,
    Font,
    Spaces,
    FontFamily,
    LineHeight,
    Borders,
    InputVariables,
    TextareaVariables,
    Code,
    WysiwygEditor,
    Link,
    Dropdown,
    BoxShadow,
    Information,
    InputGroup,
    Image,
    SharingType,
    ItemList,
    TriggerButton,
    MultiDropdown,
    SelectionType,
    ActionType,
    FacebookButtons,
    SocialIconsStyle,
    BackgroundImage,
    CustomHtml,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    fieldGroup: {
      type: Object as PropType<FieldsGroup>,
      required: true,
    },

    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    customClass: {
      type: Object,
      default: null,
    },

    itemGroup: {
      type: Object as PropType<FieldsGroup>,
      required: false,
      default: null,
    },

    subGroupLabel: {
      type: String,
      required: false,
      default: '',
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },

    nestedTabLabel: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const fieldGroupItems = computed(() => {
      if (props.itemGroup) {
        return cloneDeep(props.itemGroup.items);
      }
      if (props.fieldGroup.items) {
        const items = cloneDeep(props.fieldGroup.items);
        return items.filter((item: any) => {
          if (item.isVisible && item.parentFieldId) {
            return item.isVisible(props.fieldGroup.items, item.parentFieldId);
          }
          return true;
        });
      }
      return [];
    });

    const newParserValues = ref(cloneDeep(props.parserValues));

    // Watch for parser values changes only for fields that have watchParserValues: true
    watch(() => props.parserValues, (newValues) => {
      // Check if any field in this group has watchParserValues enabled
      const hasWatchableFields = fieldGroupItems.value?.some((field: any) => field.watchParserValues === true);
      if (hasWatchableFields) {
        newParserValues.value = cloneDeep(newValues);
      }
    }, { deep: true });

    const backupSelectedStructure = computed(() => store.getters['liveEditor/getBackupSelectedStructure']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const componentInSyncPart = computed(() => {
      if (props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.partType === props.fieldGroup.elementPartType);
      }
      return null;
    });
    const hideLoadElementBtn = computed(() => {
      if (selectedSyncElement.value && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.syncId === selectedSyncElement.value.syncElementId);
      }
      return false;
    });
    const canSaveStyle = computed(() => {
      if (!selectedSyncElement.value) {
        return true;
      }
      if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && !selectedSyncElement.value.elementPartType) {
        return true;
      }
      return false;
    });

    const handleChange = (data: any, tabItemLabel: string, fieldGroupLabel: string, fieldLabel: string, subGroupLabel?: string | undefined) => {
      if (backupSelectedStructure.value
        && ((props.isSynchronizedPage && selectedStructure.value.type === TemplateStructureEnum.PAGE) || componentInSyncPart.value)
        && !selectedSyncElement.value
        && selectedStructure.value
        && !data.init
      ) {
        const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
        const name = componentInSyncPart.value ? '' : props.headerTitle;

        store.dispatch('liveEditor/showSyncElementWarning', {
          elementId: selectedStructure.value.identifier,
          elementType,
          name,
          elementPartType: props.fieldGroup.elementPartType,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          acceptCallback: async () => {
            if (data.askConfirmation && data.properties.length > 0) {
              store.dispatch('liveEditor/showConfirmationModal', {
                title: t('confirmation'),
                message: t('templateBuilder.fields.listLayoutConfirmation'),
                acceptCallback: async () => {
                  await handlePropertyValuesChange(data, {
                    tabItemLabel,
                    fieldGroupLabel,
                    fieldLabel,
                    subGroupLabel,
                    nestedTabLabel: props.nestedTabLabel,
                  });

                  store.commit('liveEditor/setConfirmationModal', null);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValue', false);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', null);

                  await nextTick();
                  store.commit('liveEditor/refreshConfigurationPanelKey');
                },
                rejectCallback: async () => {
                  data.properties.forEach((property: any) => {
                    // eslint-disable-next-line no-param-reassign
                    property.value = data.oldValue;
                  });
                  store.commit('liveEditor/setConfirmationModalReturnToOldValue', true);
                  store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', data.properties[0].name);
                  store.commit('liveEditor/setConfirmationModal', null);

                  await nextTick();
                  store.commit('liveEditor/refreshConfigurationPanelKey');
                },
              });
            } else {
              await handlePropertyValuesChange(data, {
                tabItemLabel,
                fieldGroupLabel,
                fieldLabel,
                subGroupLabel,
                nestedTabLabel: props.nestedTabLabel,
              });
              await nextTick();
              store.commit('liveEditor/refreshConfigurationPanelKey');
            }
          },
          rejectCallback: () => {
            store.commit('liveEditor/setSelectedStructure', backupSelectedStructure.value);
            emit('refresh');
          },
        });
      } else {
        const { init } = data;
        if (init) {
          handlePropertyValuesChange(data);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (data.askConfirmation && data.properties.length > 0) {
            store.dispatch('liveEditor/showConfirmationModal', {
              title: t('confirmation'),
              message: t('templateBuilder.fields.listLayoutConfirmation'),
              acceptCallback: () => {
                handlePropertyValuesChange(data, {
                  tabItemLabel,
                  fieldGroupLabel,
                  fieldLabel,
                  subGroupLabel,
                  nestedTabLabel: props.nestedTabLabel,
                });

                store.commit('liveEditor/setConfirmationModal', null);
                store.commit('liveEditor/setConfirmationModalReturnToOldValue', false);
                store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', null);
              },
              rejectCallback: () => {
                data.properties.forEach((property: any) => {
                  // eslint-disable-next-line no-param-reassign
                  property.value = data.oldValue;
                });
                store.commit('liveEditor/setConfirmationModalReturnToOldValue', true);
                store.commit('liveEditor/setConfirmationModalReturnToOldValueProperty', data.properties[0].name);
                store.commit('liveEditor/setConfirmationModal', null);
              },
            });
          } else {
            handlePropertyValuesChange(data, {
              tabItemLabel,
              fieldGroupLabel,
              fieldLabel,
              subGroupLabel,
              nestedTabLabel: props.nestedTabLabel,
            });
          }
        }
      }
    };

    const enterEditSyncElement = () => {
      const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
      const name = componentInSyncPart.value ? '' : props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        elementPartType: props.fieldGroup.elementPartType,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    const originCall = computed(() => {
      const source = TemplateEditorState.template?.createdFromCampaign;
      if (!source) return 'templateBuilder';
      return /_automation/.test(source) ? 'automation' : 'newsletter';
    });
    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? null);

    const handleShowSaveElementModal = () => {
      store.commit('liveEditor/showSaveElementModal', {
        selector: selectedStructure.value ? `#${selectedStructure.value.identifier}` : '',
        type: selectedStructure.value ? selectedStructure.value.type : TemplateStructureEnum.WIDGET,
        elementPartType: props.fieldGroup.elementPartType,
        fromSyncElement: !canSaveStyle.value,
      });
    };

    const handleShowElementModal = () => {
      if (selectedStructure.value) {
        const template = getTemplateIframeDocument();
        if (template) {
          const element = template.querySelector(`#${selectedStructure.value.identifier}`);
          const elementSubtype = element?.getAttribute('data-widgettype') || '';
          showElementsModal([selectedStructure.value.type], {
            elementPartType: props.fieldGroup.elementPartType || '',
            elementSubtype,
            fromSyncElement: !!selectedSyncElement.value,
          });
        }
      }
    };

    const getFieldParserValues = (fieldLabel: string, isNested = false) => {
      const tabItems = newParserValues.value[props.tabItem.label]?.items as any;

      if (!tabItems) {
        return undefined;
      }

      if (props.nestedTabLabel) {
        // For nested tabs, nestedTabLabel is the parent tab label
        // and fieldGroup.label is the field group label inside
        const nestedTabItems = tabItems[props.nestedTabLabel];
        if (!nestedTabItems) {
          return undefined;
        }
        const fieldGroup = nestedTabItems[props.fieldGroup.label];
        if (!fieldGroup) {
          return undefined;
        }
        const field = fieldGroup[fieldLabel];
        return field;
      }

      if (isNested && props.subGroupLabel) {
        const subGroup = tabItems[props.subGroupLabel];
        if (!subGroup) {
          return undefined;
        }
        const fieldGroup = subGroup[props.fieldGroup.label];
        if (!fieldGroup) {
          return undefined;
        }
        const field = fieldGroup[fieldLabel];
        return field;
      }

      const fieldGroup = tabItems[props.fieldGroup.label];
      if (!fieldGroup) {
        return undefined;
      }
      const field = fieldGroup[fieldLabel];
      return field;
    };

    // Event handler methods
    const onFieldChange = (data: any, fieldLabel: string) => {
      handleChange(data, props.tabItem.label, props.fieldGroup.label, fieldLabel);
    };

    const onNestedFieldChange = (data: any, fieldLabel: string) => {
      handleChange(data, props.tabItem.label, props.fieldGroup.label, fieldLabel, props.subGroupLabel);
    };

    return {
      t,
      fieldGroupItems,
      handleChange,
      onFieldChange,
      onNestedFieldChange,
      originCall,
      idCampaign,
      componentInSyncPart,
      handleShowSaveElementModal,
      handleShowElementModal,
      hideLoadElementBtn,
      selectedSyncElement,
      canSaveStyle,
      getFieldParserValues,
      enterEditSyncElement,
    };
  },
});
</script>

<style lang="scss" scoped>
.field-wrapper {
  margin-bottom: 0 !important;

  &:deep() > div {
    margin-bottom: 1rem;
  }
}
</style>
